/**
 * @file     AuthGateModal.tsx
 * @purpose  Non-dismissible auth gate shown to unauthenticated visitors after
 *           they've viewed VIEWED_LISTINGS_THRESHOLD distinct listings.
 *           Renders SignInForm and SignUpForm inside the shared GateSheet,
 *           which slides between them (crossfade under reduced motion).
 */
'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { SignInForm } from '@/components/auth/SignInForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { GateSheet } from '@/components/gate/GateSheet';

type Step = 'signin' | 'signup';

interface Props {
  open: boolean;
  /** Optional override for what to redirect to after success. Defaults to
   *  the current pathname so the user lands back where they were. */
  redirectTo?: string;
  onAuthSuccess?: () => void;
  /** When provided, shows a close (X) button and allows backdrop/esc
   *  dismissal. Listing detail pages omit this; the gate is hard there. */
  onDismiss?: () => void;
  signupTitle?: string;
  signupSubtitle?: string;
}

/** The forms carry their own card for the standalone /sign-in pages. Inside
 *  the sheet that card is a box inside a box, so flatten it here without
 *  forking the forms. */
const embeddedFormSx = {
  '& > form, & > div': {
    p: 0,
    maxWidth: 'none',
    border: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
    backdropFilter: 'none',
  },
  '& h1': { fontSize: 24, lineHeight: 1.2, letterSpacing: '-0.015em', textAlign: 'left' },
  '& h1 + p': { textAlign: 'left' },
} as const;

export const AuthGateModal = ({
  open,
  redirectTo,
  onAuthSuccess,
  onDismiss,
  signupTitle,
  signupSubtitle,
}: Props) => {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [step, setStep] = useState<Step>('signup');

  const resolvedRedirect = redirectTo ?? pathname ?? '/';

  return (
    <GateSheet
      open={open}
      // Hard gate unless onDismiss is provided (list page is dismissible,
      // detail pages are not).
      onClose={onDismiss}
      title={
        step === 'signin'
          ? t('authGate.signinTitle', { defaultValue: 'Sign in' })
          : (signupTitle ?? t('authGate.signupTitle', { defaultValue: 'Create your account' }))
      }
      titleHidden
      statusLabel={
        step === 'signin'
          ? t('authGate.statusSignin', { defaultValue: 'Sign in' })
          : t('authGate.statusSignup', { defaultValue: 'Free account' })
      }
      stepKey={step}
      stepDirection={step === 'signin' ? 'forward' : 'back'}
      testId="auth-gate"
    >
      <Box sx={embeddedFormSx}>
        {step === 'signin' ? (
          <SignInForm
            redirectOverride={resolvedRedirect}
            onAuthSuccess={onAuthSuccess}
            onSwitchAuthMode={() => setStep('signup')}
          />
        ) : (
          <SignUpForm
            redirectOverride={resolvedRedirect}
            onAuthSuccess={onAuthSuccess}
            onSwitchAuthMode={() => setStep('signin')}
            title={signupTitle}
            subtitle={signupSubtitle}
            leadSourcePage={resolvedRedirect}
          />
        )}
      </Box>
    </GateSheet>
  );
};
